import {
  Manhole,
  SewerPipeRun,
  LateralConnection,
} from "../types/tender.types";

export const calculateManholeDepth = (
  coverLevel: number,
  invertLevel: number
): number => {
  return Math.max(0, coverLevel - invertLevel);
};

export const calculateDepthCategory = (depth: number): number => {
  return Math.round(depth / 0.25) * 0.25;
};

export const calculateRunDepthCategory = (averageDepth: number): number => {
  return Math.round(averageDepth / 0.25) * 0.25;
};

export const calculateRockers = (pipeDiameter: string): number => {
  const diameterValue = parseInt(pipeDiameter);
  return diameterValue > 0 ? 2 : 0;
};

export const calculateLateralManholeDepthCategory = (depth: number): number => {
  return Math.round(depth / 0.25) * 0.25;
};

export const calculateDepthOffMainRun = (
  mhRef: string,
  manholes: Manhole[]
): number => {
  const manhole = manholes.find((mh) => mh.ref === mhRef);
  return manhole ? manhole.depthCategory : 0;
};

export const calculateLateralAverageDepth = (
  depth: number,
  depthOffMainRun: number
): number => {
  return (depth + depthOffMainRun) / 2;
};

export const calculateAvgDepthCategory = (averageDepth: number): number => {
  return Math.round(averageDepth / 0.25) * 0.25;
};

export const calculateGullyPipeDiameter = (length: number): string => {
  return length > 0 ? "150mm" : "";
};

export const calculateGullyDepth = (gullyRef?: string): number => {
  return gullyRef && gullyRef.trim() !== "" ? 0.65 : 0.65; // Default depth when no gullyRef
};

export const calculateGullyDepthOffMainRun = (
  runDepthCategory: number
): number => {
  return runDepthCategory;
};

export const calculateGullyAverageDepth = (
  gullyDepth: number,
  depthOffMainRun: number
): number => {
  return (gullyDepth + depthOffMainRun) / 2;
};

export const calculateGulleys = (
  gulleyIntoManhole: number,
  junction150x150: number,
  junctionFromPipeRuns: number
): number => {
  return gulleyIntoManhole + junction150x150 + junctionFromPipeRuns;
};

export interface ManholeScheduleSummary {
  depthCategorySummary: {
    depthCategory: string;
    count: number;
  }[];
  diameterSummary: {
    diameter: string;
    count: number;
  }[];
  combinedSummary: {
    depthCategory: string;
    diameter: string;
    count: number;
  }[];
}

export interface PipeRunsScheduleSummary {
  depthCategorySummary: {
    depthCategory: string;
    totalLength: number;
    count: number;
  }[];
  diameterSummary: {
    diameter: string;
    totalLength: number;
    count: number;
  }[];
  combinedSummary: {
    depthCategory: string;
    diameter: string;
    totalLength: number;
    count: number;
  }[];
}

export const calculatePipeRunsScheduleSummary = (
  pipeRuns: SewerPipeRun[]
): PipeRunsScheduleSummary => {
  // Group by depth category
  const depthCategoryMap = new Map<
    string,
    { totalLength: number; count: number }
  >();
  pipeRuns.forEach((run) => {
    const category = run.depthCategory.toFixed(2);
    const existing = depthCategoryMap.get(category) || {
      totalLength: 0,
      count: 0,
    };
    existing.totalLength += run.length;
    existing.count += 1;
    depthCategoryMap.set(category, existing);
  });

  const depthCategorySummary = Array.from(depthCategoryMap.entries())
    .map(([category, data]) => ({
      depthCategory: `${category}m`,
      totalLength: data.totalLength,
      count: data.count,
    }))
    .sort((a, b) => parseFloat(a.depthCategory) - parseFloat(b.depthCategory));

  // Group by diameter
  const diameterMap = new Map<string, { totalLength: number; count: number }>();
  pipeRuns.forEach((run) => {
    const existing = diameterMap.get(run.diameter) || {
      totalLength: 0,
      count: 0,
    };
    existing.totalLength += run.length;
    existing.count += 1;
    diameterMap.set(run.diameter, existing);
  });

  const diameterSummary = Array.from(diameterMap.entries())
    .map(([diameter, data]) => ({
      diameter,
      totalLength: data.totalLength,
      count: data.count,
    }))
    .sort((a, b) => parseInt(a.diameter) - parseInt(b.diameter));

  // Combined summary (depth category × diameter)
  const combinedMap = new Map<string, { totalLength: number; count: number }>();
  pipeRuns.forEach((run) => {
    const key = `${run.depthCategory.toFixed(2)}m|${run.diameter}`;
    const existing = combinedMap.get(key) || { totalLength: 0, count: 0 };
    existing.totalLength += run.length;
    existing.count += 1;
    combinedMap.set(key, existing);
  });

  const combinedSummary = Array.from(combinedMap.entries())
    .map(([key, data]) => {
      const [depthCategory, diameter] = key.split("|");
      return {
        depthCategory,
        diameter,
        totalLength: data.totalLength,
        count: data.count,
      };
    })
    .sort((a, b) => {
      const depthA = parseFloat(a.depthCategory);
      const depthB = parseFloat(b.depthCategory);
      if (depthA !== depthB) return depthA - depthB;
      return parseInt(a.diameter) - parseInt(b.diameter);
    });

  return {
    depthCategorySummary,
    diameterSummary,
    combinedSummary,
  };
};

export const calculateManholeScheduleSummary = (
  manholes: Manhole[]
): ManholeScheduleSummary => {
  // Group by depth category
  const depthCategoryMap = new Map<string, number>();
  manholes.forEach((mh) => {
    const category = mh.depthCategory?.toFixed(2) || "0.00";
    depthCategoryMap.set(category, (depthCategoryMap.get(category) || 0) + 1);
  });

  const depthCategorySummary = Array.from(depthCategoryMap.entries())
    .map(([category, count]) => ({
      depthCategory: `${category}m`,
      count,
    }))
    .sort((a, b) => parseFloat(a.depthCategory) - parseFloat(b.depthCategory));

  // Group by diameter
  const diameterMap = new Map<string, number>();
  manholes.forEach((mh) => {
    diameterMap.set(mh.diameter, (diameterMap.get(mh.diameter) || 0) + 1);
  });

  const diameterSummary = Array.from(diameterMap.entries())
    .map(([diameter, count]) => ({
      diameter,
      count,
    }))
    .sort((a, b) => parseInt(a.diameter) - parseInt(b.diameter));

  // Combined summary (depth category × diameter)
  const combinedMap = new Map<string, number>();
  manholes.forEach((mh) => {
    const key = `${mh.depthCategory?.toFixed(2) || "0.00"}m|${mh.diameter}`;
    combinedMap.set(key, (combinedMap.get(key) || 0) + 1);
  });

  const combinedSummary = Array.from(combinedMap.entries())
    .map(([key, count]) => {
      const [depthCategory, diameter] = key.split("|");
      return {
        depthCategory,
        diameter,
        count,
      };
    })
    .sort((a, b) => {
      const depthA = parseFloat(a.depthCategory);
      const depthB = parseFloat(b.depthCategory);
      if (depthA !== depthB) return depthA - depthB;
      return parseInt(a.diameter) - parseInt(b.diameter);
    });

  return {
    depthCategorySummary,
    diameterSummary,
    combinedSummary,
  };
};

export const calculatePipeAverageDepth = (
  fromMH: Manhole | undefined,
  toMH: Manhole | undefined
): number => {
  if (!fromMH || !toMH) return 0;
  return (fromMH.depthCategory + toMH.depthCategory) / 2;
};

export const calculateTotalPipeLength = (pipeRuns: SewerPipeRun[]): number => {
  return pipeRuns.reduce((total, run) => total + (run.length || 0), 0);
};

export const validateManholeRef = (
  ref: string,
  manholes: Manhole[],
  currentId?: string
): string | null => {
  if (!ref.trim()) {
    return "Manhole reference is required";
  }

  const duplicate = manholes.find(
    (mh) => mh.ref.toLowerCase() === ref.toLowerCase() && mh.id !== currentId
  );
  if (duplicate) {
    return "Manhole reference must be unique";
  }

  return null;
};

export const validatePositiveNumber = (
  value: number,
  fieldName: string
): string | null => {
  if (value < 0) {
    return `${fieldName} must be positive`;
  }
  return null;
};

export const validatePipeRun = (
  pipeRun: SewerPipeRun,
  manholes: Manhole[]
): string[] => {
  const errors: string[] = [];

  if (!pipeRun.fromMH) {
    errors.push("From MH is required");
  }

  if (!pipeRun.toMH) {
    errors.push("To MH is required");
  }

  if (pipeRun.fromMH === pipeRun.toMH) {
    errors.push("From MH and To MH cannot be the same");
  }

  if (pipeRun.length <= 0) {
    errors.push("Length must be greater than 0");
  }

  const fromMH = manholes.find((mh) => mh.ref === pipeRun.fromMH);
  const toMH = manholes.find((mh) => mh.ref === pipeRun.toMH);

  if (pipeRun.fromMH && !fromMH) {
    errors.push("From MH reference not found");
  }

  return errors;
};
